import { useEffect } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useAuth } from "@/shared/hooks/use-auth";
import { ChatPage } from "@/modules/chat/components/ChatPage";
import { useChatStoreApi } from "@/modules/chat/stores/chat-store-context";

/**
 * Index route — chat home. Auth is gated by the `_app` layout above; by
 * the time we render here, `auth.user` may still be null only on the very
 * first render before the loading flag flips, but ChatPage tolerates a
 * null user (used purely as a guard for `useSpaceBootstrap`).
 *
 * `?sid=` carries the active session in the URL, so refresh/deep links
 * restore the right session instead of dropping the user into hero.
 * No `sid` → hero. The route component syncs URL→store synchronously in
 * render before ChatPage mounts, so `useChatStore(s => s.activeSessionId)`
 * always reflects the URL by the time hooks read it.
 */
export const Route = createFileRoute("/_app/")({
  component: IndexRoute,
  validateSearch: (search: Record<string, unknown>): { sid?: string } => {
    const v = search.sid;
    return typeof v === "string" && v.length > 0 ? { sid: v } : {};
  },
});

function IndexRoute() {
  const auth = useAuth();
  const navigate = useNavigate();
  const { sid } = Route.useSearch();
  const storeApi = useChatStoreApi();

  // Admin landing: hitting the bare root (`/` with no active session)
  // sends admins to the Company command center instead of chat hero.
  // `sid` deep-links into a chat session stay untouched, so a shared
  // session URL still opens the conversation for an admin.
  const adminLanding = !auth.loading && auth.user?.role === "admin" && !sid;
  useEffect(() => {
    if (adminLanding) navigate({ to: "/company" });
  }, [adminLanding, navigate]);
  // Sync URL → store synchronously in render. Doing it in an effect would
  // race with ChatPage's loadHistory effect (it'd fire once with the stale
  // activeId before the sync setState landed). Zustand setState during
  // render is safe — it's an external store, not React state.
  //
  // Only sync the *adopt-this-sid* direction. The reverse (URL has no sid
  // → store.activeSessionId = null) is intentionally skipped because it
  // races with `sendMessage`: between its `setState(activeId=newId)` and
  // its `navigate({sid:newId})` calls, a render with URL=`/` would see
  // store=newId vs target=null and call `setActiveSession(null)`, which
  // resets the freshly-pushed user/assistant placeholders via
  // `Object.assign(freshSessionState())` — flicker to «Начните диалог»
  // and HeroBlock until streaming events repopulate. Explicit "go to
  // hero" callers (`setActiveId(null)` in use-sessions, delete handler)
  // now call `setActiveSession(null)` themselves before navigating.
  const target = sid ?? null;
  if (target !== null && storeApi.getState().activeSessionId !== target) {
    storeApi.getState().setActiveSession(target);
  }
  if (adminLanding) return null;
  return <ChatPage user={auth.user} />;
}
